import { queryClient } from "App";
import { get } from "lodash";
import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getMachinesTableColumns,
  getMachinesTableFilters,
} from "routes/machine/components/MachinesTableColumnsAndFilters";
import TagService from "services/Tag";
import UserService from "services/User";
import { addResizedColumns } from "./useTableConfig";
import tagConstants from "constants/Tag";
import { MACHINES_TABLE_CONSTANTS } from "routes/machine/MachinesTableConstants";
import { useRuvixxFlags } from "providers/RuvixxFlagsProvider";
import { machineKeys } from "routes/machine/queryKeys";

const fetchTags = async className => {
  const taggableModels = tagConstants.taggableModels;
  const model = Object.keys(taggableModels).find(
    key => taggableModels[key] === className
  );

  const tags = await TagService.getTags({ model: model, for_filtering: true });
  return tags;
};

export const useMachinesTable = ({
  caseId,
  caseRouteId,
  entityId,
  productLineId,
  isStandalone,
  tableName = MACHINES_TABLE_CONSTANTS.tableName,
  openMapModal,
}) => {
  const { enable_aegis_automation_status } = useRuvixxFlags();

  const { data: tags, isSuccess: isTagsFetched } = useQuery({
    queryKey: ["tags", MACHINES_TABLE_CONSTANTS.className],
    queryFn: () => fetchTags(MACHINES_TABLE_CONSTANTS.className),
  });

  const { data: tableSettings, isSuccess: isTableSettingsFetched } = useQuery({
    queryKey: ["table-settings"],
    queryFn: UserService.getUserSettings,
  });

  // Distinct per view so invalidating one table's data leaves the others alone,
  // while machineKeys.all still reaches all of them.
  const queryKey = useMemo(() => {
    if (entityId && caseId) return machineKeys.list({ entityId, caseId });
    if (entityId) return machineKeys.list({ entityId });
    if (caseRouteId) return machineKeys.list({ caseRouteId });
    return machineKeys.list();
  }, [caseRouteId, entityId, caseId]);

  const invalidateData = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey });
  }, [queryKey]);

  const filters = useMemo(() => {
    if (!isTagsFetched) {
      return [];
    }
    return getMachinesTableFilters({
      tags,
      caseId,
      entityId,
      productLineId,
      isStandalone,
      enableAegisAutomationStatus: enable_aegis_automation_status,
    });
  }, [
    tags,
    caseId,
    entityId,
    productLineId,
    isStandalone,
    isTagsFetched,
    enable_aegis_automation_status,
  ]);

  const columns = useMemo(() => {
    if (!isTableSettingsFetched) return [];
    const initialColumns = getMachinesTableColumns({
      caseRouteId,
      openMapModal,
      invalidateData,
      enableAegisAutomationStatus: enable_aegis_automation_status,
    });

    const machinesTableSettings = get(
      tableSettings,
      `user_settings.preferences.tables.${tableName}`,
      {}
    );
    const withResizedColumns = addResizedColumns({
      columns: initialColumns,
      resizedColumns: machinesTableSettings?.resized_columns || {},
    });

    return withResizedColumns;
  }, [
    caseRouteId,
    openMapModal,
    tableName,
    tableSettings,
    isTableSettingsFetched,
    invalidateData,
    enable_aegis_automation_status,
  ]);

  return { filters, columns, tags, queryKey, invalidateData };
};
